"use client";

import React, { useState } from "react";
import { Sparkles, ArrowRight, Atom, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { RangeInput } from "@/components/ui/range-input";
import { Reveal } from "@/components/cosmic/Reveal";
export default function WhatIfUniverse() {
  const [nuclearForce, setNuclearForce] = useState<number>(102);
  const [dimensions, setDimensions] = useState<number>(4);
  return <section id="what-if-universe" data-nav="dark" className="relative py-16 sm:py-20 lg:py-28 w-full max-w-full overflow-x-clip text-foreground">
      <div className="relative z-10 mx-auto max-w-[76rem] w-full px-4 sm:px-6">
        {/* Subtle section hairline divider */}
        <div className="ql-divider mb-16" aria-hidden="true" />

        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-10">
          {/* Columns 1–6: Portal-style Visual Window */}
          <div className="order-2 lg:order-1 lg:col-span-6">
            <Reveal>
              <div className="relative mx-auto max-w-lg lg:max-w-none rounded-3xl p-2 sm:p-3 ql-glass-strong shadow-2xl">
                <div className="overflow-hidden rounded-2xl border border-border/50 relative bg-card/60">
                  <AspectRatio ratio={1 / 1}>
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/portal_event_horizon.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/portal_event_horizon.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/portal_event_horizon.png-wvc-sizes" alt="Circular luminous cosmic portal warping space and light in deep space" width={1000} height={1000} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                    {/* Concentric event horizon rings overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent pointer-events-none" />

                    <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 flex flex-col min-[380px]:flex-row items-start min-[380px]:items-center justify-between gap-1.5 rounded-xl border border-border/60 bg-card/90 px-3 py-2 sm:px-4 sm:py-2.5 backdrop-blur-md text-[11px] sm:text-xs font-mono text-card-foreground">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Atom className="size-3.5 sm:size-4 text-primary shrink-0 animate-spin [animation-duration:12s]" />
                        <span className="truncate">STATUS: COHERENT</span>
                      </div>
                      <span className="text-primary font-bold shrink-0">
                        D={dimensions} | αs={(nuclearForce / 100).toFixed(2)}x
                      </span>
                    </div>
                  </AspectRatio>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Columns 7–12: Parameters & Explanatory Prose */}
          <div className="order-1 lg:order-2 lg:col-span-6 flex flex-col justify-center">
            <Reveal delay={120}>
              <div className="flex items-center gap-2 mb-4 font-mono text-xs font-semibold tracking-wider text-primary uppercase">
                <Sparkles className="size-3.5" />
                <span>COSMIC PARAMETER LAB</span>
              </div>

              <h2 className="font-serif text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl text-foreground leading-[1.12]">
                Recalibrate Fundamental Constants
              </h2>

              <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
                What happens if the strong nuclear force strengthens by two percent, or spatial dimensions expand to five? Alter reality's baseline constants and observe what physics predicts.
              </p>

              {/* Interactive Levers Container */}
              <div className="mt-8 rounded-2xl border border-border/60 bg-card/60 p-5 sm:p-6 backdrop-blur-md space-y-5">
                <div className="flex items-center justify-between border-b border-border/40 pb-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-card-foreground">
                    <SlidersHorizontal className="size-4 text-primary" />
                    <span>Interactive Calibration Matrix</span>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">PREVIEW LEVERS</span>
                </div>

                {/* Lever 1 */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-mono text-card-foreground">
                    <span>Strong Nuclear Force Constant</span>
                    <span className="text-primary font-semibold">
                      {nuclearForce > 100 ? `+${nuclearForce - 100}%` : `${nuclearForce - 100}%`}
                    </span>
                  </div>
                  <RangeInput min={80} max={120} step={1} value={nuclearForce} onValueChange={val => setNuclearForce(val)} className="accent-primary cursor-pointer touch-none" aria-label="Strong Nuclear Force Constant" />
                </div>

                {/* Lever 2 */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-mono text-card-foreground">
                    <span>Spatial Dimensions</span>
                    <span className="text-primary font-semibold">{dimensions} Dimensions</span>
                  </div>
                  <RangeInput min={3} max={6} step={1} value={dimensions} onValueChange={val => setDimensions(val)} className="accent-primary cursor-pointer touch-none" aria-label="Spatial Dimensions" />
                </div>
              </div>

              {/* Primary Action Button */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 items-stretch sm:items-start w-full sm:w-auto">
                <Link to="/WhatIfUniversePage" className="ql-btn w-full sm:w-auto min-h-[44px] justify-center">
                  <span>Launch What-If Simulator</span>
                  <ArrowRight className="size-4 shrink-0" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>;
}